import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  ListItemButton,
} from "@mui/material";
import {
  People,
  School,
  Payment,
  Receipt,
  Class as ClassIcon,
  CheckCircle,
  Timeline,
  PersonAdd,
  Book,
  Work,
  BarChart,
  Business,
  HistoryEdu,
  ExitToApp,
  Block,
  Man,
  Woman,
  Schedule,
  Assessment,
  MenuBookOutlined,
  Gavel,
  MenuBook,
  Mail,
  LocalLibrary,
  Settings,
  Tune,
  Language,
  Archive,
} from "@mui/icons-material";

import { useRecoilState, useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';

import NoteAddIcon from '@mui/icons-material/NoteAdd';

import { useWindowSize } from 'react-use';
import { useTheme } from '@mui/material/styles';

import { isFalsy } from 'utility-types';
import { useNavigate } from 'react-router-dom';

import { colorsAtom, currentBasicTextFilterPropsAtom, currentFormNameAtom, currentUserSessionAtom, isSaveLoadingAtom, isSearchBoxShowAtom } from 'library/store';

import useMenu from 'components/services/Menu';

import useSchoolYearService, { useBasicFilterSchoolYear } from 'features/setup/services/SchoolYear';
import { defaultSchoolYear, ISchoolYear } from 'features/setup/models/SchoolYear';

import { BasicTextFilterForm, defaultBasicTextFilterProps } from 'components/ui/BasicTextFilterForm';

import useRegistrationService from 'features/production/services/Registration';
import { IBillingDashboard, defaultBillingDashboard } from 'features/finance/models/Billing';


import { QuickActions } from "./widgets/QuickActions";
import { defaultRegistrationDashboard, IRegistrationDashboard } from './production/models/Registration';

import { iconFromName } from 'components/ui/DynamicIcon';

export const Home = () => {

  const theme = useTheme();

  const { t, i18n } = useTranslation();

  const navigate = useNavigate();

  const { applicationMenus: menus, setApplicationMenus: setMenus } = useMenu();
  const menuItems = menus.flatMap(mnu => mnu.menuItems);

  const [currentFormName, setCurrentFormName] = useRecoilState(currentFormNameAtom);
  const [isSaveLoading, setIsSaveLoading] = useRecoilState(isSaveLoadingAtom);
  const [Colors, setColors] = useRecoilState(colorsAtom);

  const { language: lg, schoolYears, applicationSetup, roleEntities } = useRecoilValue(currentUserSessionAtom);
  const [schoolYear, setSchoolYear] = useState<ISchoolYear>(
    schoolYears.find(s => s.id === applicationSetup.currentSchoolYearId) ?? defaultSchoolYear);

  const [isSearchBoxShow, setIsSearchBoxShow] = useRecoilState(isSearchBoxShowAtom);
  const [currentBasicTextFilterProps, setCurrentBasicTextFilterProps] = useRecoilState(currentBasicTextFilterPropsAtom);

  const { getDashboard } = useRegistrationService();

  const [dashboard, setDashboard] = useState<IRegistrationDashboard>(defaultRegistrationDashboard);

  const [kpis, setKpis] = useState<{ label: string, value: number, icon: React.ReactElement, color: string }[]>([]);

  const basicFilterSchoolYear = useBasicFilterSchoolYear(
    async (event: React.MouseEvent<unknown>, row: ISchoolYear) => {
      setIsSearchBoxShow(false);
      setSchoolYear(schoolYears.find(s => s.id === row.id) ?? defaultSchoolYear);

      setCurrentFormName(row.name);
      const dash = await getDashboard(row.id);
      setDashboard(dash);
    }
  );

  const [openDialog, setOpenDialog] = useState(false);
  const [activeNav, setActiveNav] = useState("");
  const [activeNavLabel, setActiveNavLabel] = useState("");

  const handleThematicNavigation = (name: string, label: string) => {
    setActiveNav(name);
    setActiveNavLabel(label);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  // Navigation
  const navigation = [
    { name: "schooling", label: t('Schooling'), icon: <School /> },
    { name: "humanResources", label: t('Human Resources'), icon: <People /> },
    { name: "pedagogical", label: t('Pedagogical Activities'), icon: <MenuBook /> },
    { name: "timetable", label: t('Timetable'), icon: <Schedule /> },
    { name: "finance", label: t('Finance'), icon: <Payment /> },
    { name: "evaluation", label: t('Evaluation'), icon: <Assessment /> },
    { name: "institution", label: t('Institution'), icon: <Business /> },
    { name: "teaching", label: t('Teaching'), icon: <MenuBookOutlined /> },
    { name: "disciplinary", label: t('Disciplinary rec.'), icon: <Gavel /> },
    { name: "messaging", label: t('Messaging'), icon: <Mail /> },
    { name: "library", label: t('Library'), icon: <LocalLibrary /> },
    { name: "settings", label: t('Settings'), icon: <Settings /> },
    { name: "archives", label: t('Archives'), icon: <Archive /> },
    { name: "kampuxOnline", label: t('Kampux online'), icon: <Language /> },
    { name: "configuration", label: t('Configuration'), icon: <Tune /> },
    { name: "reports", label: t('Reports'), icon: <BarChart /> },

  ];


  const handleNavigationLink = async (routeToGo: string) => {
    //event.preventDefault();
    navigate(`${routeToGo}/0`);
  }

  useEffect(() => {
    setCurrentFormName(schoolYear.name);
    setCurrentBasicTextFilterProps(basicFilterSchoolYear);
    setIsSaveLoading(false);
  }, []);

  useEffect(() => {
    async function _() {

      setActiveNav(navigation[0].name);
      setActiveNavLabel(navigation[0].label);

      setSchoolYear(schoolYears.find(s => s.id === applicationSetup.currentSchoolYearId) ?? defaultSchoolYear);
      const dash = await getDashboard(applicationSetup.currentSchoolYearId);
      setDashboard(dash);
    }
    _();
  }, []);

  useEffect(() => {
    setKpis([
      {
        label: t('Students'),
        value: dashboard.countStudents,
        icon: <People />,
        color: '#a5d8ff', // pastel blue
      },
      {
        label: t('Girls'),
        value: dashboard.countGirls,
        icon: <Woman />,
        color: '#ffd6e0', // pastel pink
      },
      {
        label: t('Boys'),
        value: dashboard.countBoys,
        icon: <Man />,
        color: '#d0f4de', // pastel green
      },
      {
        label: t('News'),
        value: dashboard.countNewStudents,
        icon: <PersonAdd />,
        color: '#fff3bf', // pastel yellow
      },
      {
        label: t('Olds'),
        value: dashboard.countOldStudents,
        icon: <HistoryEdu />,
        color: '#e0c3fc', // pastel purple
      },
      {
        label: t('Pre-registrations'),
        value: dashboard.countPreregistrations,
        icon: <Payment />,
        color: '#caffbf', // pastel mint
      },
      {
        label: t('Withdrawns'),
        value: dashboard.countWithdrawnStudents,
        icon: <ExitToApp />,
        color: '#ffdfba', // pastel orange
      },
      {
        label: t('Expels'),
        value: dashboard.countExpelStudents,
        icon: <Block />,
        color: '#ffc9de', // pastel rose
      },
    ]);
  }, [dashboard, lg, t]);

  return (
    <Box sx={{ p: 2 }}>
      {/* KPI Top Row */}
      <Grid container spacing={1} sx={{ mb: 1 }}>
        {kpis.map((kpi, index) => (
          <Grid item xs={6} sm={3} md={1.5} key={index}>
            <Card sx={{
              textAlign: "center",
              border: "2px solid",
              borderColor: "grey.300", // or use theme color like 'primary.main'
              borderRadius: 2,
              boxShadow: "none", // optional, remove shadow if you just want border
              backgroundColor: kpi.color,
            }}>
              <CardContent>
                {kpi.icon}
                <Typography variant="h6">{kpi.value}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {kpi.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={1} sx={{ mt: 0.5, alignItems: 'flex-start' }}>
        {/* Left Column Quick Access */}
        <Grid item xs={12} sm={3}>
          <Card sx={{ p: 1, height: '100%' }}>
            <CardContent sx={{ p: 1 }}>
              <Typography variant="h6">{t('Quick access')}</Typography>

              <QuickActions />
            </CardContent>
          </Card>
        </Grid>

        {/* Center Navigation */}
        <Grid item xs={12} sm={6}>
          <Card sx={{ p: 1, height: '100%' }}>
            <CardContent sx={{ p: 1 }}>
              <Typography variant="h6">{t('Navigation')}</Typography>
              <Grid container spacing={1} sx={{ mt: 1 }}>
                {navigation.map((nav, i) => (
                  <Grid item xs={6} sm={6} md={3} key={i}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => handleThematicNavigation(nav.name, nav.label)}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        height: 120, // make it square-like, adjust as needed
                        textTransform: "none",
                        borderRadius: 2,
                        borderWidth: activeNav === nav.name ? 2 : 1,
                        borderColor: activeNav === nav.name ? "primary.main" : undefined,
                        //backgroundColor: activeNav === nav.name ? "primary.light" : undefined,
                      }}
                      startIcon={null} // remove the default startIcon
                    >
                      <Box sx={{ mb: 1 }}>{nav.icon}</Box>
                      <Typography variant="body1">{nav.label}</Typography>
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column Recent Activity */}
        <Grid item xs={12} sm={3}>
          <Card sx={{ p: 1, height: '100%' }}>
            <CardContent sx={{ p: 1 }}>
              <Typography variant="h6">{activeNavLabel}</Typography>
              <List sx={{ p: 0, '& .MuiListItemButton-root': { transition: 'all 200ms ease', borderRadius: 1.2, my: 0.75, py: 1.2, px: 1.5, '&:hover': { backgroundColor: 'action.hover', transform: 'translateX(4px)', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }, '& .MuiSvgIcon-root': { fontSize: 24, opacity: 0.75 }, '& .MuiListItemText-primary': { fontWeight: 600, fontSize: '0.95rem', color: '#1a1a1a' } } }}>
                {menuItems.filter(mnuItm => roleEntities.some(re => re.entityName === mnuItm.entityName) &&
                  (mnuItm.thematics || []).some(x => x.name === activeNav)).map((mnuItm, i) => (
                    <Tooltip title={mnuItm.toolTip || ''} key={`${i} - ${mnuItm.name}`}>
                      <ListItemButton onClick={() => handleNavigationLink(mnuItm.routeToGo)}>
                        {iconFromName(mnuItm.iconName || '', Colors.menuIcon)}
                        <ListItemText primary={mnuItm.text} sx={{ ml: 1 }} />
                      </ListItemButton>
                    </Tooltip>
                  ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>


    </Box>
  );
}